import { createSlice } from "@reduxjs/toolkit";

const NotificationsSlice = createSlice({
  name: "notifications",
  initialState: {
    notifications: [],
    unreadCount: 0,
    isNotificationsDrawerOpen: false,
    lastAction: null, // Track the type of last action
    isLoading: false,
    error: null,
    hasMore: true,
    pageNumber: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
    isFetchingMore: false,
    lastFetchError: null, // Separate error for fetch operations
    // Epoch ms of the last successful first-page load. The Header is
    // re-mounted by every screen, so this is what lets a route change reuse
    // an already-fresh list instead of refetching — see useNotifications.
    lastFetchedAt: null,
  },
  reducers: {
    // Set all notifications
    setNotifications: (state, action) => {
      state.notifications = action.payload.notifications;
      // Calculate unreadCount from all notifications, not just the payload
      state.unreadCount = action.payload.notifications.filter(
        (n) => !n.isRead
      ).length;
      state.totalCount = action.payload.totalCount;
      state.totalPages = action.payload.totalPages;
      state.pageNumber = action.payload.pageNumber;
      state.hasMore = action.payload.pageNumber < action.payload.totalPages;
      state.lastFetchError = null;
      state.lastFetchedAt = Date.now();
    },

    // Add more notifications for pagination
    addMoreNotifications: (state, action) => {
      const newNotifications = [
        ...state.notifications,
        ...action.payload.notifications,
      ];
      state.notifications = newNotifications;
      // Calculate unreadCount from all notifications, not just the new ones
      state.unreadCount = newNotifications.filter((n) => !n.isRead).length;
      state.totalCount = action.payload.totalCount;
      state.totalPages = action.payload.totalPages;
      state.pageNumber = action.payload.pageNumber;
      state.hasMore = action.payload.pageNumber < action.payload.totalPages;
      state.isFetchingMore = false;
      state.lastFetchError = null;
    },

    // Add a new notification
    addNotification: (state, action) => {
      state.notifications.unshift(action.payload);
      if (!action.payload.isRead) {
        state.unreadCount += 1;
      }
      state.totalCount += 1;
      state.totalPages = Math.ceil(state.totalCount / state.pageSize);
      state.lastAction = "add";
    },

    // Mark a notification as read
    markAsRead: (state, action) => {
      const notificationId = action.payload;
      const notification = state.notifications.find(
        (n) => n.id === notificationId
      );

      if (notification && !notification.isRead) {
        notification.isRead = true;
        state.unreadCount -= 1;
        state.lastAction = "markAsRead";
      }
    },

    // Mark all notifications as read
    markAllAsRead: (state) => {
      state.notifications.forEach((notification) => {
        notification.isRead = true;
      });
      state.unreadCount = 0;
      state.lastAction = "markAllAsRead";
    },

    // Delete a notification
    deleteNotification: (state, action) => {
      const notificationId = action.payload;
      const notificationIndex = state.notifications.findIndex(
        (n) => n.id === notificationId
      );

      if (notificationIndex !== -1) {
        const notification = state.notifications[notificationIndex];
        if (!notification.isRead) {
          state.unreadCount -= 1;
        }
        state.notifications.splice(notificationIndex, 1);
        state.totalCount -= 1;
        state.totalPages = Math.ceil(state.totalCount / state.pageSize);
        state.lastAction = "delete";
      }
    },

    // Clear all notifications
    clearAllNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
      state.totalCount = 0;
      state.totalPages = 0;
      state.hasMore = true;
      state.pageNumber = 1;
      state.lastAction = "clearAll";
    },

    // Set notifications drawer open state
    setNotificationsDrawerOpen: (state, action) => {
      state.isNotificationsDrawerOpen = action.payload;
    },

    // Set loading state
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },

    // Set error
    setError: (state, action) => {
      state.error = action.payload;
    },

    // Set fetch error
    setFetchError: (state, action) => {
      state.lastFetchError = action.payload;
    },

    // Reset last action
    resetLastAction: (state) => {
      state.lastAction = null;
    },

    // Update a notification (for real-time updates)
    updateNotification: (state, action) => {
      const { notificationId, updatedProperties } = action.payload;
      const notificationIndex = state.notifications.findIndex(
        (n) => n.id === notificationId
      );

      if (notificationIndex !== -1) {
        const originalNotification = state.notifications[notificationIndex];
        const wasUnread = !originalNotification.isRead;
        const isUnread =
          updatedProperties.isRead === undefined
            ? wasUnread
            : !updatedProperties.isRead;

        // Update the notification
        state.notifications[notificationIndex] = {
          ...originalNotification,
          ...updatedProperties,
        };

        // Update unread count if needed
        if (wasUnread && !isUnread) {
          state.unreadCount -= 1;
        } else if (!wasUnread && isUnread) {
          state.unreadCount += 1;
        }

        state.lastAction = "update";
      }
    },

    // Batch update notifications (for initial load)
    batchUpdateNotifications: (state, action) => {
      const notifications = action.payload;
      state.notifications = notifications;
      state.unreadCount = notifications.filter((n) => !n.isRead).length;
      state.lastAction = "batchUpdate";
    },

    // Set fetching more state for pagination
    setFetchingMore: (state, action) => {
      state.isFetchingMore = action.payload;
    },

    // Increment page number
    incrementPageNumber: (state) => {
      state.pageNumber += 1;
    },

    // Reset pagination state
    resetPagination: (state) => {
      state.pageNumber = 1;
      state.hasMore = true;
      state.isFetchingMore = false;
      state.lastFetchError = null;
    },

    // Clear errors
    clearErrors: (state) => {
      state.error = null;
      state.lastFetchError = null;
    },

    // Update unread count from all notifications
    updateUnreadCount: (state) => {
      state.unreadCount = state.notifications.filter((n) => !n.isRead).length;
    },
  },
});

export const {
  setNotifications,
  addMoreNotifications,
  addNotification,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  clearAllNotifications,
  setNotificationsDrawerOpen,
  setLoading,
  setError,
  setFetchError,
  resetLastAction,
  updateNotification,
  batchUpdateNotifications,
  setFetchingMore,
  incrementPageNumber,
  resetPagination,
  clearErrors,
  updateUnreadCount,
} = NotificationsSlice.actions;

export default NotificationsSlice.reducer;
